import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useEffect } from "react";

const SUBPROCESSORS = [
  { name: "Emergent", role: "Infrastructure applicative et passerelle LLM (accès à l'API OpenAI)" },
  { name: "Cloudflare", role: "Reverse proxy, sécurisation du trafic, mécanismes de challenge et protection anti-bot / anti-DDoS selon les services activés" },
  { name: "OpenAI", role: "Génération des reformulations via l'API Chat Completions" },
];

export default function Legal() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF9F6] grain">
      <header className="border-b-2 border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="legal-back"
            className="text-sm font-medium flex items-center gap-2 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </Link>
          <h1 className="font-display font-semibold text-base sm:text-xl uppercase tracking-tight">
            cordialement<span className="text-[#4A4A4A]">.</span>ai
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-12 relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="dossier-tag">Mentions légales & confidentialité</span>
          <span className="flex-1 h-px bg-black/20 max-w-[160px]" />
          <span className="dossier-tag">Dossier n° CDLT-2026</span>
        </div>

        <h2 className="hero-title text-black text-[38px] sm:text-[54px] mb-2">
          <span className="block font-bold">Mentions légales</span>
          <span className="block italic font-semibold text-[#2A4A3E]">
            & confidentialité.
          </span>
        </h2>
        <p className="dossier-tag mb-10">Dernière mise à jour : septembre 2026</p>

        <section className="bg-[#B5EAD7] border-2 border-black nb-shadow p-5 mb-10 flex gap-3 items-start">
          <ShieldCheck className="w-6 h-6 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-display font-semibold text-lg mb-2">
              Zéro compte. Aucun tracker publicitaire. Aucun cookie analytics.
            </p>
            <p className="text-sm text-black leading-relaxed">
              cordialement.ai fonctionne sans inscription et a été conçu pour limiter au
              maximum la collecte et la conservation de données. Nous ne créons aucun profil
              utilisateur et ne conservons pas le contenu de vos messages dans notre base de
              données. Pour générer une reformulation, le texte que vous saisissez doit
              néanmoins être traité temporairement par notre infrastructure et transmis au
              service d&apos;intelligence artificielle utilisé par cordialement.ai. Nous
              vous recommandons donc de ne pas saisir d&apos;informations confidentielles,
              sensibles ou inutilement nominatives.
            </p>
          </div>
        </section>

        <section className="space-y-3 mb-10">
          <h3 className="font-display font-semibold text-xl">Éditeur</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            cordialement.ai est édité par :
          </p>
          <ul className="text-sm text-[#4A4A4A] leading-relaxed list-none space-y-1 pl-0">
            <li><strong>IN SITU PRODUCTIONS</strong></li>
            <li>SASU au capital de 1 000 €</li>
            <li className="pt-1"><span className="font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]">Siège social</span></li>
            <li>26 rue Piat</li>
            <li>75020 Paris</li>
            <li>France</li>
            <li className="pt-2"><span className="font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]">SIREN</span> — 941 684 912</li>
            <li><span className="font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]">RCS</span> — Paris, inscrit le 13/03/2025 (RCS-RNE)</li>
            <li><span className="font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]">TVA intracommunautaire</span> — FR73941684912</li>
            <li className="pt-2">Directeur de la publication : <strong>Clovis Henriot</strong></li>
            <li>Téléphone : <a href="tel:+33973661364" className="underline">09 73 66 13 64</a></li>
            <li>Contact : <a href="mailto:contact@leteletravailleur.fr" className="underline">contact@leteletravailleur.fr</a></li>
          </ul>
          <p className="text-sm text-[#4A4A4A] leading-relaxed mt-2">
            <em>Le Télétravailleur</em> est le nom utilisé pour l&apos;activité éditoriale
            et créative de Clovis Henriot / In Situ Productions.
          </p>
        </section>

        <section className="space-y-3 mb-10">
          <h3 className="font-display font-semibold text-xl">Hébergement & infrastructure</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            L&apos;application cordialement.ai repose sur plusieurs prestataires techniques
            nécessaires à son fonctionnement. L&apos;infrastructure applicative est
            notamment opérée via <strong>Emergent</strong>, qui fournit l&apos;environnement
            d&apos;hébergement et certains services techniques permettant le fonctionnement
            de l&apos;application. <strong>Cloudflare</strong> assure des fonctions de
            sécurisation du trafic (reverse proxy, challenges de sécurité, protection
            anti-bot, WAF/anti-DDoS selon les services réellement activés) et de
            protection contre les usages abusifs.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Les communications entre votre navigateur et l&apos;infrastructure sont
            <strong> chiffrées en transit via HTTPS/TLS</strong>.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Certaines opérations techniques peuvent impliquer des traitements ou transferts
            de données hors de l&apos;Espace économique européen. Lorsque cela est
            nécessaire, ces transferts sont encadrés par les mécanismes de protection
            applicables prévus par le RGPD et par les accords conclus avec nos prestataires.
            Les informations précises relatives aux lieux de traitement et garanties
            contractuelles applicables sont celles prévues dans les conditions
            contractuelles et accords de traitement des données conclus avec chaque
            prestataire.
          </p>
        </section>

        <section className="space-y-3 mb-10">
          <h3 className="font-display font-semibold text-xl">Conditions Générales d&apos;Utilisation</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            cordialement.ai est un service <strong>gratuit</strong> permettant de reformuler
            ou rédiger des messages professionnels à l&apos;aide d&apos;un modèle
            d&apos;intelligence artificielle fourni par OpenAI. Le service peut notamment
            être utilisé pour reformuler un message que vous souhaitez envoyer ou préparer
            une réponse à un message reçu dans un contexte professionnel.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Les textes générés sont des suggestions automatisées. cordialement.ai ne
            garantit pas leur exactitude, leur pertinence, leur exhaustivité ou leur
            conformité à votre situation particulière. Vous restez seul responsable du
            contenu que vous décidez d&apos;envoyer à vos correspondants et devez relire
            toute suggestion avant utilisation.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Un quota partagé de <strong>80 générations par jour</strong> est appliqué à
            l&apos;échelle du service afin de maîtriser les coûts d&apos;utilisation de
            l&apos;intelligence artificielle. Ce quota est réinitialisé chaque nuit à
            minuit UTC et peut évoluer sans préavis.
          </p>
        </section>

        <section className="space-y-4 mb-10">
          <h3 className="font-display font-semibold text-xl">Données personnelles</h3>

          <h4 className="font-display font-semibold text-base mt-2">Notre principe</h4>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            cordialement.ai est conçu selon les principes de <em>minimisation des données</em>
            {" "}et de <em>protection des données dès la conception</em>. Nous cherchons à ne
            traiter que les informations strictement nécessaires au fonctionnement et à la
            sécurisation du service.
          </p>

          <h4 className="font-display font-semibold text-base mt-4">Aucun compte utilisateur</h4>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Aucune création de compte ou authentification n&apos;est nécessaire. Nous ne
            demandons pas votre nom, votre adresse email ou votre numéro de téléphone pour
            utiliser le service.
          </p>

          <h4 className="font-display font-semibold text-base mt-4">Contenu des messages</h4>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Le texte que vous soumettez est traité afin de produire la reformulation
            demandée. Il n&apos;est <strong>pas enregistré dans la base de données</strong>
            {" "}applicative de cordialement.ai après la génération de la réponse. Il doit
            néanmoins être transmis aux prestataires techniques intervenant dans la
            génération, notamment au service d&apos;intelligence artificielle utilisé par
            l&apos;application.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Si votre texte contient des informations permettant d&apos;identifier une
            personne — par exemple un nom, une adresse email, une fonction professionnelle
            ou toute autre information personnelle — ces données peuvent donc être
            temporairement traitées dans le cadre de cette opération. Nous vous
            recommandons de supprimer ou anonymiser toute information nominative qui
            n&apos;est pas nécessaire à la reformulation.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            <strong>N&apos;utilisez pas cordialement.ai</strong> pour transmettre des
            données sensibles, des secrets professionnels, des informations médicales,
            financières ou toute autre information confidentielle qui n&apos;est pas
            nécessaire à la génération du message.
          </p>

          <h4 className="font-display font-semibold text-base mt-4">OpenAI</h4>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Les reformulations sont générées à l&apos;aide de l&apos;API OpenAI, sur
            l&apos;endpoint <em>Chat Completions</em>. Chaque requête est indépendante :
            l&apos;application n&apos;utilise ni conversations persistantes, ni threads,
            ni assistants avec historique, ni fichiers, ni vector stores. Sur l&apos;API
            Chat Completions, le paramètre <code className="bg-[#F3EFE7] px-1 border border-black/20">store</code>{" "}
            est <strong>normalement désactivé par défaut</strong> lorsqu&apos;il n&apos;est
            pas explicitement demandé : cordialement.ai ne demande pas volontairement le
            stockage des réponses côté OpenAI.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            OpenAI indique que les données envoyées via son API ne sont pas utilisées pour
            entraîner ou améliorer ses modèles par défaut, sauf activation volontaire
            d&apos;un mécanisme de partage de données par le client de l&apos;API.
            Conformément à sa politique API standard, OpenAI peut conserver certains
            prompts et réponses <strong>jusqu&apos;à 30 jours</strong> dans ses journaux
            de surveillance des abus, notamment à des fins de sécurité, de prévention des
            abus et de fourniture du service, sauf lorsqu&apos;une configuration
            spécifique de conservation différente est applicable.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            L&apos;accès à OpenAI est intermédié par la passerelle LLM d&apos;Emergent
            (Emergent LLM Gateway). Le comportement interne exact de cette passerelle
            (notamment le paramétrage effectif appliqué aux appels OpenAI en aval)
            n&apos;est pas entièrement observable depuis le code de l&apos;application :
            nous ne formulons donc pas de garantie absolue sur les paramètres appliqués
            au-delà de cette passerelle. cordialement.ai n&apos;utilise pas volontairement
            les données de ses utilisateurs pour entraîner les modèles OpenAI.
          </p>

          <h4 className="font-display font-semibold text-base mt-4">Absence de tracking publicitaire ou comportemental</h4>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            cordialement.ai n&apos;intègre aucun outil de publicité ciblée ou de suivi
            comportemental de type Google Analytics, Meta Pixel, Hotjar, PostHog ou
            équivalent. Aucune régie publicitaire n&apos;est intégrée au service.
          </p>

          <h4 className="font-display font-semibold text-base mt-4">Polices de caractères</h4>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Les polices IBM Plex Sans et IBM Plex Mono sont <strong>auto-hébergées</strong>.
            Leur affichage ne nécessite donc aucun appel vers Google Fonts.
          </p>

          <h4 className="font-display font-semibold text-base mt-4">Journaux applicatifs et adresses IP</h4>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            cordialement.ai n&apos;a pas vocation à créer un historique nominatif de vos
            utilisations. Les journaux applicatifs susceptibles de contenir le contenu des
            messages ou les réponses générées sont désactivés. Les journaux d&apos;accès
            réseau du serveur (qui enregistrent habituellement les adresses IP des
            visiteurs) sont également désactivés au démarrage de l&apos;application.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Pour prévenir les abus (rafales de requêtes, robots), un mécanisme de
            limitation de débit fonctionne <strong>en mémoire vive</strong> et utilise
            temporairement l&apos;adresse IP de l&apos;appelant sur une fenêtre glissante
            de quelques secondes. Ces compteurs ne sont pas enregistrés en base de
            données, ne servent pas à constituer un profil et sont perdus au redémarrage
            du serveur. Base : intérêt légitime de l&apos;éditeur à sécuriser son service.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Nos prestataires d&apos;infrastructure et de sécurité (notamment Cloudflare)
            traitent nécessairement, de leur côté, certaines données techniques telles que
            l&apos;adresse IP, des informations sur le navigateur ou des données permettant
            de détecter un trafic automatisé ou malveillant, conformément à leurs propres
            politiques et aux mécanismes prévus par le RGPD.
          </p>

          <h4 className="font-display font-semibold text-base mt-4">Quota</h4>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Un <strong>compteur global</strong> est utilisé afin de suivre le nombre total
            de générations réalisées chaque jour. Ce compteur n&apos;a pas pour finalité
            d&apos;identifier les utilisateurs ni de constituer un historique individuel
            d&apos;utilisation : il ne contient qu&apos;un entier (nombre de générations)
            associé à une date.
          </p>

          <h4 className="font-display font-semibold text-base mt-4">Historique local et préférences</h4>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Pour votre confort, votre navigateur peut conserver localement vos{" "}
            <strong>20 dernières reformulations</strong> dans son espace{" "}
            <code className="bg-[#F3EFE7] px-1.5 py-0.5 border border-black/20">localStorage</code>,
            ainsi que quelques préférences d&apos;interface : dernier mode sélectionné,
            langue, position du curseur de diplomatie et niveau de franchise du mode
            Reverse. Ces informations restent stockées <strong>dans votre navigateur
            uniquement</strong> et ne sont pas synchronisées avec un compte cordialement.ai.
            Vous pouvez les supprimer à tout moment via le bouton
            &laquo; Purger les archives &raquo; ou en supprimant les données du site
            depuis votre navigateur.
          </p>
        </section>

        <section className="space-y-3 mb-10">
          <h3 className="font-display font-semibold text-xl">Finalités et bases juridiques</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Les traitements réalisés dans le cadre de cordialement.ai poursuivent
            principalement les finalités suivantes :
          </p>
          <ul className="text-sm text-[#4A4A4A] leading-relaxed list-disc pl-5 space-y-2">
            <li>
              <strong>Fourniture du service de reformulation</strong> — traitement du
              texte soumis afin de produire la réponse demandée. Base : exécution du
              service demandé par l&apos;utilisateur.
            </li>
            <li>
              <strong>Sécurité et prévention des abus</strong> — protection de
              l&apos;application contre les attaques, robots, utilisations frauduleuses et
              consommations anormales de ressources. Base : intérêt légitime de
              l&apos;éditeur à sécuriser son infrastructure et à prévenir les abus.
            </li>
          </ul>
        </section>

        <section className="space-y-3 mb-10">
          <h3 className="font-display font-semibold text-xl">Destinataires et sous-traitants</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Les données strictement nécessaires au fonctionnement du service peuvent être
            traitées par les prestataires techniques suivants :
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-2 border-black text-sm">
              <thead className="bg-[#F3EFE7]">
                <tr className="border-b-2 border-black">
                  <th className="font-mono text-[10px] uppercase tracking-widest p-2">Prestataire</th>
                  <th className="font-mono text-[10px] uppercase tracking-widest p-2">Fonction</th>
                </tr>
              </thead>
              <tbody>
                {SUBPROCESSORS.map((s, i) => (
                  <tr key={s.name} className={i < SUBPROCESSORS.length - 1 ? "border-b border-black/20" : ""}>
                    <td className="p-2 font-medium text-[#0A0A0A]">{s.name}</td>
                    <td className="p-2 text-[#4A4A4A]">{s.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3">
            Ces prestataires disposent de leurs propres infrastructures et peuvent recourir
            à des sous-traitants techniques. Certaines données peuvent être traitées en
            dehors de l&apos;Espace économique européen dans les conditions prévues par les
            accords applicables avec ces prestataires et les mécanismes de transfert prévus
            par le RGPD.
          </p>
        </section>

        <section className="space-y-3 mb-10">
          <h3 className="font-display font-semibold text-xl">Cookies et technologies nécessaires</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            cordialement.ai <strong>ne dépose aucun cookie applicatif, publicitaire ou de
            mesure d&apos;audience</strong>. Cloudflare peut déposer des cookies strictement
            nécessaires à la sécurité et à la protection du service, notamment{" "}
            <code className="bg-[#F3EFE7] px-1.5 py-0.5 border border-black/20">cf_clearance</code>
            {" "}lors de certains challenges de sécurité et{" "}
            <code className="bg-[#F3EFE7] px-1.5 py-0.5 border border-black/20">__cf_bm</code>
            {" "}dans le cadre de ses mécanismes de détection des robots. Ces cookies ne
            sont pas utilisés par cordialement.ai à des fins publicitaires ou de profilage
            marketing.
          </p>
        </section>

        <section className="space-y-3 mb-10">
          <h3 className="font-display font-semibold text-xl">Vos droits</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Dans la mesure où des données personnelles sont traitées dans le cadre de
            l&apos;utilisation de cordialement.ai, vous disposez des droits prévus par la
            réglementation applicable, notamment, selon les circonstances, des droits
            d&apos;accès, de rectification, d&apos;effacement, de limitation ou
            d&apos;opposition.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            En raison de l&apos;absence de compte utilisateur et de l&apos;absence de
            stockage du contenu des messages dans notre base de données, cordialement.ai
            peut ne disposer d&apos;aucune donnée permettant de retrouver a posteriori une
            requête ou de la rattacher à une personne déterminée.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Pour toute question ou demande concernant la protection de vos données :{" "}
            <a href="mailto:contact@leteletravailleur.fr" className="underline">
              contact@leteletravailleur.fr
            </a>
            . Vous disposez également du droit d&apos;introduire une réclamation auprès de
            la Commission nationale de l&apos;informatique et des libertés (CNIL).
          </p>
        </section>

        <section className="space-y-3 mb-10">
          <h3 className="font-display font-semibold text-xl">Limitation de responsabilité</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Les contenus générés par une intelligence artificielle peuvent contenir des
            erreurs, des approximations, des biais ou des formulations inadaptées au
            contexte. cordialement.ai constitue une aide à la rédaction et ne remplace pas
            votre propre appréciation.
          </p>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            L&apos;utilisateur reste responsable des messages qu&apos;il choisit
            d&apos;envoyer et de leur conformité aux lois, obligations professionnelles,
            engagements contractuels et règles internes qui lui sont applicables. Relisez
            toujours votre message avant de l&apos;envoyer.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="font-display font-semibold text-xl">Contact</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed">
            Pour toute question relative au service ou à la protection des données :{" "}
            <a href="mailto:contact@leteletravailleur.fr" className="underline">
              contact@leteletravailleur.fr
            </a>
            .
          </p>
        </section>
      </main>
    </div>
  );
}
